/**
 * UX-024: Image Lightbox Component
 * Full-screen image viewer with zoom, pinch-to-zoom, swipe navigation, and preloading
 */

import React, { useState, useEffect, useCallback } from 'react';
import { X, ZoomIn, ZoomOut, ChevronLeft, ChevronRight } from 'lucide-react';
import { getGalleryImageUrl, getCardThumbnailUrl } from '../../utils/imageUrl';

interface ImageLightboxProps {
  images: string[];
  initialIndex: number;
  onClose: () => void;
  title?: string;
}

const ImageLightbox: React.FC<ImageLightboxProps> = ({
  images,
  initialIndex,
  onClose,
  title
}) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  // Pinch-to-zoom state
  const [lastPinchDist, setLastPinchDist] = useState<number | null>(null);

  // Swipe navigation state (when not zoomed)
  const [swipeStart, setSwipeStart] = useState<number | null>(null);

  // Helper to calculate distance between two touch points
  const getTouchDistance = (touches: React.TouchList): number => {
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const handlePrev = useCallback(() => {
    setCurrentIndex(prev => (prev === 0 ? images.length - 1 : prev - 1));
    setScale(1);
    setPosition({ x: 0, y: 0 });
  }, [images.length]);

  const handleNext = useCallback(() => {
    setCurrentIndex(prev => (prev === images.length - 1 ? 0 : prev + 1));
    setScale(1);
    setPosition({ x: 0, y: 0 });
  }, [images.length]);

  const toggleZoom = () => {
    if (scale === 1) {
      setScale(2);
    } else {
      setScale(1);
      setPosition({ x: 0, y: 0 });
    }
  };

  const handleZoomIn = () => {
    setScale(prev => Math.min(prev + 0.5, 4));
  };

  const handleZoomOut = () => {
    if (scale <= 1) {
      setScale(1);
      setPosition({ x: 0, y: 0 });
    } else {
      setScale(prev => Math.max(prev - 0.5, 1));
    }
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') handlePrev();
      if (e.key === 'ArrowRight') handleNext();
      if (e.key === '+' || e.key === '=') handleZoomIn();
      if (e.key === '-') handleZoomOut();
    };

    window.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [onClose, handlePrev, handleNext]);

  // Preload adjacent images for smooth navigation (using optimized URLs)
  useEffect(() => {
    const preload = (src: string) => {
      const img = new Image();
      img.src = getGalleryImageUrl(src);
    };
    if (currentIndex > 0) preload(images[currentIndex - 1]);
    if (currentIndex < images.length - 1) preload(images[currentIndex + 1]);
  }, [currentIndex, images]);

  // Drag handling for panning zoomed images
  const handleMouseDown = (e: React.MouseEvent) => {
    if (scale > 1) {
      setIsDragging(true);
      setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging && scale > 1) {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Touch handlers for mobile (panning when zoomed)
  const [touchStart, setTouchStart] = useState<{ x: number; y: number } | null>(null);

  const handleTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      // Pinch start
      e.preventDefault();
      setLastPinchDist(getTouchDistance(e.touches));
    } else if (e.touches.length === 1) {
      if (scale > 1) {
        // Pan start (existing behavior)
        setTouchStart({
          x: e.touches[0].clientX - position.x,
          y: e.touches[0].clientY - position.y
        });
      } else {
        // Swipe start (for navigation when not zoomed)
        setSwipeStart(e.touches[0].clientX);
      }
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (e.touches.length === 2 && lastPinchDist !== null) {
      // Pinch zoom
      e.preventDefault();
      const newDist = getTouchDistance(e.touches);
      const delta = newDist / lastPinchDist;
      setScale(prev => Math.min(Math.max(prev * delta, 1), 4));
      setLastPinchDist(newDist);
    } else if (e.touches.length === 1) {
      if (touchStart && scale > 1) {
        // Pan (existing behavior)
        setPosition({
          x: e.touches[0].clientX - touchStart.x,
          y: e.touches[0].clientY - touchStart.y
        });
      }
    }
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (lastPinchDist !== null) {
      setLastPinchDist(null);
      // Snap to 1x if close to it
      if (scale < 1.15) {
        setScale(1);
        setPosition({ x: 0, y: 0 });
      }
      return;
    }

    setTouchStart(null);

    // Swipe navigation (only when not zoomed)
    if (scale === 1 && swipeStart !== null) {
      const swipeEnd = e.changedTouches[0]?.clientX ?? swipeStart;
      const diff = swipeStart - swipeEnd;
      if (Math.abs(diff) > 50) {
        if (diff > 0) handleNext();
        else handlePrev();
      }
      setSwipeStart(null);
    }
  };

  return (
    <div className="fixed inset-0 bg-black z-[100] flex flex-col">
      {/* Header */}
      <div className="flex-shrink-0 bg-black/80 backdrop-blur-sm px-4 py-3 flex items-center justify-between z-10">
        <div className="flex items-center gap-4">
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/10 rounded-full transition text-white"
            aria-label="Close"
          >
            <X className="h-6 w-6" />
          </button>
          <span className="text-white/80 text-sm font-medium">
            {currentIndex + 1} / {images.length}
          </span>
        </div>

        {title && (
          <span className="text-white/60 text-sm truncate max-w-[200px] hidden sm:block">
            {title}
          </span>
        )}

        {/* Zoom controls */}
        <div className="flex items-center gap-2">
          <button
            onClick={handleZoomOut}
            disabled={scale <= 1}
            className="p-2 hover:bg-white/10 rounded-full transition text-white disabled:opacity-30 disabled:cursor-not-allowed"
            aria-label="Zoom out"
          >
            <ZoomOut className="h-5 w-5" />
          </button>
          <span className="text-white/60 text-xs w-12 text-center">
            {Math.round(scale * 100)}%
          </span>
          <button
            onClick={handleZoomIn}
            disabled={scale >= 4}
            className="p-2 hover:bg-white/10 rounded-full transition text-white disabled:opacity-30 disabled:cursor-not-allowed"
            aria-label="Zoom in"
          >
            <ZoomIn className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Main image area */}
      <div
        className="flex-1 relative overflow-hidden select-none"
        style={{ touchAction: 'none' }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Navigation arrows */}
        {images.length > 1 && (
          <>
            <button
              onClick={handlePrev}
              className="absolute left-4 top-1/2 -translate-y-1/2 p-3 bg-black/50 hover:bg-black/70 rounded-full text-white z-10 transition"
              aria-label="Previous image"
            >
              <ChevronLeft className="h-6 w-6" />
            </button>
            <button
              onClick={handleNext}
              className="absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-black/50 hover:bg-black/70 rounded-full text-white z-10 transition"
              aria-label="Next image"
            >
              <ChevronRight className="h-6 w-6" />
            </button>
          </>
        )}

        {/* Image */}
        <div
          className="w-full h-full flex items-center justify-center"
          onClick={() => scale === 1 && toggleZoom()}
          style={{ cursor: scale === 1 ? 'zoom-in' : scale > 1 && isDragging ? 'grabbing' : 'grab' }}
        >
          <img
            src={getGalleryImageUrl(images[currentIndex])}
            alt={`Image ${currentIndex + 1}`}
            className="max-w-full max-h-full object-contain transition-transform duration-200"
            loading="eager"
            style={{
              transform: `scale(${scale}) translate(${position.x / scale}px, ${position.y / scale}px)`,
            }}
            draggable={false}
          />
        </div>
      </div>

      {/* Thumbnails */}
      {images.length > 1 && (
        <div className="flex-shrink-0 bg-black/80 backdrop-blur-sm p-3">
          <div className="flex justify-center gap-2 overflow-x-auto max-w-full scrollbar-hide">
            {images.map((img, i) => (
              <button
                key={i}
                onClick={() => {
                  setCurrentIndex(i);
                  setScale(1);
                  setPosition({ x: 0, y: 0 });
                }}
                className={`flex-shrink-0 w-16 h-12 rounded-lg overflow-hidden transition ${
                  i === currentIndex
                    ? 'ring-2 ring-white'
                    : 'opacity-50 hover:opacity-80'
                }`}
              >
                <img
                  src={getCardThumbnailUrl(img)}
                  alt={`Thumbnail ${i + 1}`}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Instructions */}
      <div className="absolute bottom-20 left-1/2 -translate-x-1/2 text-white/40 text-xs text-center pointer-events-none">
        <span className="hidden sm:inline">Use arrow keys to navigate • +/- to zoom • ESC to close</span>
        <span className="sm:hidden">Tap image to zoom • Swipe to navigate</span>
      </div>
    </div>
  );
};

export default ImageLightbox;
